import React, { useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import { X } from 'lucide-react';
import { cn } from '../../lib/utils';

function Dialog({ open, onOpenChange, children }) {
  const handleEscape = useCallback(
    (e) => {
      if (e.key === 'Escape' && onOpenChange) {
        onOpenChange(false);
      }
    },
    [onOpenChange],
  );

  useEffect(() => {
    if (open) {
      document.addEventListener('keydown', handleEscape);
      document.body.style.overflow = 'hidden';
    }
    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = '';
    };
  }, [open, handleEscape]);

  if (!open) return null;

  return children;
}

Dialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onOpenChange: PropTypes.func,
  children: PropTypes.node.isRequired,
};

Dialog.defaultProps = {
  onOpenChange: null,
};

function DialogContent({ className, children, onClose, ...props }) {
  return (
    <div className="fixed inset-0 z-50">
      {/* Overlay */}
      {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions */}
      <div className="fixed inset-0 bg-black/80 animate-in fade-in-0" onClick={onClose} />

      {/* Content */}
      <div
        className={cn(
          'fixed left-[50%] top-[50%] z-50 grid w-full max-w-lg translate-x-[-50%] translate-y-[-50%]',
          'gap-4 border bg-background p-6 shadow-lg duration-200 sm:rounded-lg',
          'animate-in fade-in-0 zoom-in-95 slide-in-from-left-1/2 slide-in-from-top-[48%]',
          className,
        )}
        style={{ borderColor: 'hsl(var(--border))' }}
        {...props}
      >
        {children}
        {onClose && (
          <button
            type="button"
            onClick={onClose}
            className={cn(
              'absolute right-4 top-4 rounded-sm opacity-70 ring-offset-background transition-opacity',
              'hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2',
            )}
          >
            <X className="h-4 w-4" />
            <span className="sr-only">Close</span>
          </button>
        )}
      </div>
    </div>
  );
}

DialogContent.propTypes = {
  className: PropTypes.string,
  children: PropTypes.node.isRequired,
  onClose: PropTypes.func,
};

DialogContent.defaultProps = {
  className: '',
  onClose: null,
};

function DialogHeader({ className, ...props }) {
  return (
    <div
      className={cn('flex flex-col space-y-1.5 text-center sm:text-left', className)}
      {...props}
    />
  );
}

DialogHeader.propTypes = {
  className: PropTypes.string,
};

DialogHeader.defaultProps = {
  className: '',
};

function DialogFooter({ className, ...props }) {
  return (
    <div
      className={cn('flex flex-col-reverse sm:flex-row sm:justify-end sm:space-x-2', className)}
      {...props}
    />
  );
}

DialogFooter.propTypes = {
  className: PropTypes.string,
};

DialogFooter.defaultProps = {
  className: '',
};

function DialogTitle({ className, children, ...props }) {
  return (
    <h2 className={cn('text-lg font-semibold leading-none tracking-tight', className)} {...props}>
      {children}
    </h2>
  );
}

DialogTitle.propTypes = {
  className: PropTypes.string,
  children: PropTypes.node.isRequired,
};

DialogTitle.defaultProps = {
  className: '',
};

function DialogDescription({ className, ...props }) {
  return <p className={cn('text-sm text-muted-foreground', className)} {...props} />;
}

DialogDescription.propTypes = {
  className: PropTypes.string,
};

DialogDescription.defaultProps = {
  className: '',
};

export { Dialog, DialogContent, DialogHeader, DialogFooter, DialogTitle, DialogDescription };
